import * as THREE from 'three';
import {
  Object3D,
  Scene,
  Group,
  Material,
  Mesh,
  MeshStandardMaterial,
  Color,
  Texture,
  TextureLoader,
  Vector3,
  Box3,
  PlaneGeometry,
  SphereGeometry,
} from 'three';

export interface ObjectMaterialInfo {
  id: string;
  material: Material;
  originalProperties: {
    color: Color;
    roughness: number;
    metalness: number;
    map?: Texture | null;
  };
  userData: any;
}

export interface ObjectInfo {
  id: string;
  name: string;
  object: Object3D;
  type: 'mesh' | 'group' | 'light' | 'camera' | 'other';
  materials: string[];
  userData: any;
}

export interface GeometryInfo {
  id: string;
  name: string;
  geometry: THREE.BufferGeometry;
  type: 'plane' | 'sphere' | 'box' | 'custom';
  userData: any;
}

export class ObjectControl {
  private _scene: Scene | null = null;
  private _mainGroup: Group | null = null;
  private _objects: Map<string, ObjectInfo> = new Map();
  private _materials: Map<string, ObjectMaterialInfo> = new Map();
  private _geometries: Map<string, GeometryInfo> = new Map();
  private _textureLoader: TextureLoader = new TextureLoader();
  private _nextObjectId: number = 1;
  private _nextMaterialId: number = 1;
  private _nextGeometryId: number = 1;

  // 콜백 함수들
  public _onObjectAdded: ((objectInfo: ObjectInfo) => void) | null = null;
  public _onObjectRemoved: ((objectId: string) => void) | null = null;
  public _onObjectSelected: ((objectId: string) => void) | null = null;
  public _onMaterialChanged: ((materialId: string, property: string, value: any) => void) | null =
    null;
  public _onGeometryCreated: ((geometryInfo: GeometryInfo) => void) | null = null;

  constructor() {}

  /**
   * Scene 설정
   */
  public setScene(scene: Scene): void {
    this._scene = scene;
    this.setupMainGroup();
  }

  /**
   * 메인 그룹 설정
   */
  public setupMainGroup(): void {
    if (!this._scene) return;
    this._mainGroup = new Group();
    this._mainGroup.name = 'MainGroup';
    this._scene.add(this._mainGroup);
  }

  /**
   * 오브젝트 추가
   */
  public addObject(object: Object3D | Group, name?: string, userData?: any): string {
    if (!this._mainGroup) return '';

    const id = this.generateObjectId();
    const objectName = name || `${object.type}_${id}`;
    const objectType = this.getObjectType(object);

    const objectInfo: ObjectInfo = {
      id,
      name: objectName,
      object,
      type: objectType,
      materials: [],
      userData: userData || {},
    };

    // 머티리얼 추출 및 등록
    this.extractMaterialsFromObject(object, objectInfo);

    this._objects.set(id, objectInfo);
    this._mainGroup.add(object);

    if (this._onObjectAdded) {
      this._onObjectAdded(objectInfo);
    }

    return id;
  }

  /**
   * 오브젝트 제거
   */
  public removeObject(objectId: string): boolean {
    const objectInfo = this._objects.get(objectId);
    if (!objectInfo || !this._mainGroup) return false;

    // 머티리얼 정리
    objectInfo.materials.forEach((materialId) => {
      this.removeMaterial(materialId);
    });

    // 오브젝트 제거
    this._mainGroup.remove(objectInfo.object);
    this._objects.delete(objectId);

    if (this._onObjectRemoved) {
      this._onObjectRemoved(objectId);
    }

    return true;
  }

  public getMainObject(): Object3D | null {
    return this._mainGroup;
  }

  /**
   * 머티리얼 색상 변경
   */
  public setMaterialColor(materialId: string, color: string | number): boolean {
    const materialInfo = this._materials.get(materialId);
    if (!materialInfo) return false;

    const material = materialInfo.material as MeshStandardMaterial;
    if (material.color) {
      material.color.set(color);
      material.needsUpdate = true;

      if (this._onMaterialChanged) {
        this._onMaterialChanged(materialId, 'color', color);
      }
    }

    return true;
  }

  /**
   * 머티리얼 텍스처 변경
   */
  public async setMaterialTexture(
    materialId: string,
    textureUrl: string,
    textureType: 'map' | 'normalMap' | 'roughnessMap' | 'metalnessMap' | 'aoMap' | 'emissiveMap',
  ): Promise<boolean> {
    const materialInfo = this._materials.get(materialId);
    if (!materialInfo) return false;

    try {
      const texture = await this.loadTexture(textureUrl);
      const material = materialInfo.material as MeshStandardMaterial;

      switch (textureType) {
        case 'map':
          material.map = texture;
          break;
        case 'normalMap':
          material.normalMap = texture;
          break;
        case 'roughnessMap':
          material.roughnessMap = texture;
          break;
        case 'metalnessMap':
          material.metalnessMap = texture;
          break;
        case 'aoMap':
          material.aoMap = texture;
          break;
        case 'emissiveMap':
          material.emissiveMap = texture;
          break;
      }

      material.needsUpdate = true;

      if (this._onMaterialChanged) {
        this._onMaterialChanged(materialId, textureType, textureUrl);
      }

      return true;
    } catch (error) {
      console.error('텍스처 로딩 실패:', error);
      return false;
    }
  }

  /**
   * 머티리얼 속성 변경
   */
  public setMaterialProperty(materialId: string, property: string, value: any): boolean {
    const materialInfo = this._materials.get(materialId);
    if (!materialInfo) return false;

    const material = materialInfo.material as any;
    if (material[property] !== undefined) {
      material[property] = value;
      material.needsUpdate = true;

      if (this._onMaterialChanged) {
        this._onMaterialChanged(materialId, property, value);
      }

      return true;
    }

    return false;
  }

  /**
   * 머티리얼 원본 상태로 복원
   */
  public resetMaterial(materialId: string): boolean {
    const materialInfo = this._materials.get(materialId);
    if (!materialInfo) return false;

    const material = materialInfo.material as MeshStandardMaterial;
    const original = materialInfo.originalProperties;

    if (material.color && original.color) {
      material.color.copy(original.color);
    }
    if (material.roughness !== undefined) {
      material.roughness = original.roughness;
    }
    if (material.metalness !== undefined) {
      material.metalness = original.metalness;
    }
    if (material.map !== original.map) {
      material.map = original.map || null;
    }

    material.needsUpdate = true;

    if (this._onMaterialChanged) {
      this._onMaterialChanged(materialId, 'reset', 'original');
    }

    return true;
  }

  /**
   * 바닥 생성
   */
  public createFloor(
    width: number = 1000,
    height: number = 1000,
    color: number = 0x000000,
    roughness: number = 0.5,
    metalness: number = 0.5,
    opacity: number = 0,
    position: Vector3 = new Vector3(0, -0.1, 0),
  ): string {
    const geometry = new PlaneGeometry(width, height);
    const material = new MeshStandardMaterial({
      color,
      roughness,
      metalness,
      transparent: true,
      opacity,
    });

    const floor = new Mesh(geometry, material);
    floor.rotation.x = -Math.PI / 2;
    floor.position.copy(position);
    floor.name = 'Floor';
    floor.renderOrder = 0;

    return this.addObject(floor, 'Floor');
  }

  /**
   * 애니메이션 스팟 생성
   */
  public createAnimationSpot(
    radius: number = 0.043,
    color: number = 0xffffff,
    opacity: number = 0.48,
    position: Vector3 = new Vector3(0, 0, 0),
  ): string {
    const geometry = new SphereGeometry(radius, 32, 16);
    const material = new MeshStandardMaterial({
      color,
      side: THREE.DoubleSide,
      opacity,
      transparent: true,
    });

    const spot = new Mesh(geometry, material);
    spot.position.copy(position);
    spot.name = 'AnimationSpot';

    return this.addObject(spot, 'AnimationSpot');
  }

  /**
   * 바운딩 박스 계산
   */
  public calculateBoundingBox(objectId: string): Box3 | null {
    const objectInfo = this._objects.get(objectId);
    if (!objectInfo) return null;

    const boundingBox = new Box3();
    boundingBox.setFromObject(objectInfo.object);
    return boundingBox;
  }

  /**
   * 오브젝트 크기 계산
   */
  public calculateObjectSize(objectId: string): Vector3 | null {
    const boundingBox = this.calculateBoundingBox(objectId);
    if (!boundingBox) return null;

    const size = new Vector3();
    boundingBox.getSize(size);
    return size;
  }

  /**
   * 오브젝트 중심점 계산
   */
  public calculateObjectCenter(objectId: string): Vector3 | null {
    const boundingBox = this.calculateBoundingBox(objectId);
    if (!boundingBox) return null;

    const center = new Vector3();
    boundingBox.getCenter(center);
    return center;
  }

  /**
   * 충돌 감지
   */
  public checkCollision(objectId1: string, objectId2: string): boolean {
    const box1 = this.calculateBoundingBox(objectId1);
    const box2 = this.calculateBoundingBox(objectId2);

    if (!box1 || !box2) return false;

    return box1.intersectsBox(box2);
  }

  /**
   * 거리 계산
   */
  public calculateDistance(objectId1: string, objectId2: string): number | null {
    const objectInfo1 = this._objects.get(objectId1);
    const objectInfo2 = this._objects.get(objectId2);

    if (!objectInfo1 || !objectInfo2) return null;

    const position1 = new Vector3();
    const position2 = new Vector3();

    objectInfo1.object.getWorldPosition(position1);
    objectInfo2.object.getWorldPosition(position2);

    return position1.distanceTo(position2);
  }

  /**
   * 모든 오브젝트 정리
   */
  public clearAllObjects(): void {
    if (this._mainGroup) {
      this._mainGroup.clear();
    }

    // 머티리얼 정리
    this._materials.forEach((materialInfo) => {
      this.disposeMaterial(materialInfo.material);
    });

    // 기하학 정리
    this._geometries.forEach((geometryInfo) => {
      geometryInfo.geometry.dispose();
    });

    this._objects.clear();
    this._materials.clear();
    this._geometries.clear();
  }

  /**
   * 머티리얼 정리
   */
  private disposeMaterial(material: Material): void {
    for (const key in material) {
      const value = (material as any)[key];
      if (value && typeof value === 'object' && 'isTexture' in value) {
        value.dispose();
      }
    }
    material.dispose();
  }

  /**
   * 텍스처 로딩
   */
  private async loadTexture(url: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
      this._textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.format = THREE.RGBAFormat;
          texture.flipY = false;
          texture.anisotropy = 8;
          texture.minFilter = THREE.LinearMipMapLinearFilter;
          texture.magFilter = THREE.LinearFilter;
          resolve(texture);
        },
        undefined,
        reject,
      );
    });
  }

  /**
   * 오브젝트에서 머티리얼 추출
   */
  private extractMaterialsFromObject(object: Object3D, objectInfo: ObjectInfo): void {
    object.traverse((child) => {
      if (child instanceof Mesh && child.material) {
        const material = child.material;
        if (Array.isArray(material)) {
          material.forEach((mat) => this.registerMaterial(mat, objectInfo));
        } else {
          this.registerMaterial(material, objectInfo);
        }
      }
    });
  }

  /**
   * 머티리얼 등록
   */
  private registerMaterial(material: Material, objectInfo: ObjectInfo): void {
    const id = this.generateMaterialId();

    const materialInfo: ObjectMaterialInfo = {
      id,
      material,
      originalProperties: {
        color: (material as MeshStandardMaterial).color?.clone() || new Color(),
        roughness: (material as MeshStandardMaterial).roughness || 0.5,
        metalness: (material as MeshStandardMaterial).metalness || 0.5,
        map: (material as MeshStandardMaterial).map,
      },
      userData: {},
    };

    this._materials.set(id, materialInfo);
    objectInfo.materials.push(id);
  }

  /**
   * 머티리얼 제거
   */
  private removeMaterial(materialId: string): void {
    const materialInfo = this._materials.get(materialId);
    if (materialInfo) {
      this.disposeMaterial(materialInfo.material);
      this._materials.delete(materialId);
    }
  }

  /**
   * 오브젝트 타입 판별
   */
  private getObjectType(object: Object3D): 'mesh' | 'group' | 'light' | 'camera' | 'other' {
    if (object instanceof Mesh) return 'mesh';
    if (object instanceof Group) return 'group';
    if (object instanceof THREE.Light) return 'light';
    if (object instanceof THREE.Camera) return 'camera';
    return 'other';
  }

  /**
   * ID 생성 함수들
   */
  private generateObjectId(): string {
    return `obj_${this._nextObjectId++}`;
  }

  private generateMaterialId(): string {
    return `mat_${this._nextMaterialId++}`;
  }

  private generateGeometryId(): string {
    return `geo_${this._nextGeometryId++}`;
  }

  // Getter 메서드들
  public getScene(): Scene | null {
    return this._scene;
  }

  public getMainGroup(): Group | null {
    return this._mainGroup;
  }

  public getObject(objectId: string): ObjectInfo | undefined {
    return this._objects.get(objectId);
  }

  public getMaterial(materialId: string): ObjectMaterialInfo | undefined {
    return this._materials.get(materialId);
  }

  public getAllObjects(): Map<string, ObjectInfo> {
    return this._objects;
  }

  public getAllMaterials(): Map<string, ObjectMaterialInfo> {
    return this._materials;
  }

  /**
   * 리소스 정리
   */
  public dispose(): void {
    this.clearAllObjects();
    this._scene = null;
    this._mainGroup = null;
  }
}
